import React, { useState } from "react";
import Slider from "@mui/material/Slider";
import { styled } from "@mui/material/styles";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";

import { AiOutlinePercentage, AiOutlineCalendar } from "react-icons/ai";
import { LangContext } from "../../context/LangContext";
import "./emiCalculator.scss";

ChartJS.register(ArcElement, Tooltip, Legend);

const SlideMarks = {
  marksTenure: [
    { value: 0, label: "0" },
    { value: 90, label: "90" },
    { value: 180, label: "180" },
    { value: 270, label: "270" },
    { value: 360, label: "360" },
  ],
  marksInt: [
    { value: 0, label: "0" },
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 20, label: "20" },
  ],
  marksAmt: [
    { value: 0, label: "0" },
    { value: 5000000, label: "50L" },
    { value: 10000000, label: "1Cr" },
    { value: 15000000, label: "1.5Cr" },
    { value: 20000000, label: "2Cr" },
  ],
};

const SSlider = styled(Slider)({
  color: "#003399",
  height: 8,
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "inherit",
    },
    "&:before": {
      display: "none",
    },
  },
});

const EMICalculator = () => {
  const lang = React.useContext(LangContext);
  const np = lang.state.np;

  const [pAmount, setpAmount] = useState(6000000);
  const [interest, setInterest] = useState(7);
  const [duration, setDuration] = useState(24);

  const maxValue = 20000000;
  const intMax = 20;
  const maxDuration = 360;

  const intr = interest / 1200;
  const emi = duration
    ? Math.round((pAmount * intr) / (1 - Math.pow(1 / (1 + intr), duration)))
    : 0;
  const totalAmt = duration * emi;
  var TotalAmountOfCredit = Math.round(
    (emi / intr) * (1 - Math.pow(1 + intr, -duration))
  );
  const TotalAmountOfInterest = totalAmt - TotalAmountOfCredit;

  const commaEMI = emi.toLocaleString("en-IN");

  const commapAmount = pAmount.toLocaleString("en-IN");

  const commaTotalAmountOfInterest =
    TotalAmountOfInterest.toLocaleString("en-IN");

  const commatotalAmt = totalAmt.toLocaleString("en-IN");

  const chartData = {
    labels: ["Total Interest", "Principal Loan Amount"],
    datasets: [
      {
        label: "",
        data: [TotalAmountOfInterest, pAmount],
        backgroundColor: ["rgba(221, 17, 52, 0.5)", "rgba(0, 51, 153, 0.5)"],
        borderColor: ["rgba(221, 17, 52, 1)", "rgba(0, 51, 153, 1)"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <div className="emiCalculatorMainContainer">
      <div className="emiCalculatorInputMainContainer">
        {/* amount */}
        <div className="emiCalculatorInputFlexItem">
          <div className="emiCalculatorAboveSlider">
            <div className="emiCalculatorSliderTitleContainer">
              <label htmlFor="emi-loan-amount" className="emiCalculatorSliderTitleText">
                {np ? "ऋण रकम" : "Loan Amount"}
              </label>
              <p className="emiCalculatorSliderTitleSubText">
                {np ? "रुपैयाँमा" : "in rupees"}
              </p>
            </div>
            <div className="emiCalculatorAmountInputContainer">
              <input
                className="emiCalculatorAmountInput"
                id="emi-loan-amount"
                value={pAmount}
                type="number"
                onChange={(e) => setpAmount(e.target.value)}
                aria-label={np ? "ऋण रकम" : "Loan Amount"}
              />
              <div className="emiCalculatorAmountInputButton" aria-hidden="true">
                {np ? "रु" : "Rs"}
              </div>
            </div>
          </div>

          <SSlider
            value={pAmount}
            marks={SlideMarks.marksAmt}
            onChange={(event, vAmt) => {
              setpAmount(vAmt);
            }}
            defaultValue={pAmount}
            max={maxValue}
            valueLabelDisplay="auto"
            aria-label={np ? "ऋण रकम स्लाइडर" : "Loan amount slider"}
          />
        </div>

        {/* interest */}
        <div className="emiCalculatorInputFlexItem">
          <div className="emiCalculatorAboveSlider">
            <div className="emiCalculatorSliderTitleContainer">
              <label htmlFor="emi-interest-rate" className="emiCalculatorSliderTitleText">
                {np ? "ब्याजदर" : "Interest Rate"}
              </label>
              <p className="emiCalculatorSliderTitleSubText">
                {np ? "*प्रति वर्ष" : "*per annum"}
              </p>
            </div>
            <div className="emiCalculatorAmountInputContainer">
              <input
                className="emiCalculatorAmountInput"
                id="emi-interest-rate"
                value={interest}
                type="number"
                onChange={(e) => setInterest(e.target.value)}
                aria-label={np ? "ब्याजदर" : "Interest Rate"}
              />
              <div className="emiCalculatorAmountInputButton" aria-hidden="true">
                <AiOutlinePercentage />
              </div>
            </div>
          </div>

          <SSlider
            value={interest}
            marks={SlideMarks.marksInt}
            onChange={(event, vInt) => {
              setInterest(vInt);
            }}
            defaultValue={pAmount}
            max={intMax}
            valueLabelDisplay="auto"
            aria-label={np ? "ब्याजदर स्लाइडर" : "Interest rate slider"}
          />
        </div>

        {/* duration */}
        <div className="emiCalculatorInputFlexItem">
          <div className="emiCalculatorAboveSlider">
            <div className="emiCalculatorSliderTitleContainer">
              <label htmlFor="emi-loan-tenure" className="emiCalculatorSliderTitleText">
                {np ? "ऋण अवधि" : "Loan Tenure"}
              </label>
              <p className="emiCalculatorSliderTitleSubText">
                {np ? "महिनामा" : "in months"}
              </p>
            </div>
            <div className="emiCalculatorAmountInputContainer">
              <input
                className="emiCalculatorAmountInput"
                id="emi-loan-tenure"
                value={duration}
                type="number"
                onChange={(e) => setDuration(e.target.value)}
                aria-label={np ? "ऋण अवधि" : "Loan Tenure"}
              />
              <div className="emiCalculatorAmountInputButton" aria-hidden="true">
                <AiOutlineCalendar />
              </div>
            </div>
          </div>

          <SSlider
            value={duration}
            marks={SlideMarks.marksTenure}
            onChange={(event, vDur) => {
              setDuration(vDur);
            }}
            defaultValue={duration}
            max={maxDuration}
            valueLabelDisplay="auto"
            aria-label={np ? "ऋण अवधि स्लाइडर" : "Loan tenure slider"}
          />
        </div>
      </div>

      <div className="emiCalculatorResultContainer">
        <h4 className="emiCalculatorResultTitle">
          {np ? "मासिक किस्ता" : "Monthly EMI"}
        </h4>
        <h4 className="emiCalculatorResultMonthlyEMI">
          {np ? "रु." : "Rs."} {commaEMI}
        </h4>

        <div className="emiCalculatorResultRowContainer">
          <p className="emiCalculatorResultRowText">
            {np ? "प्रिन्सिपल" : "Principal"}
          </p>
          <p className="emiCalculatorResultRowAns">
            {np ? "रु." : "Rs."} {commapAmount ? commapAmount : 0}
          </p>
        </div>

        <div className="emiCalculatorResultRowContainer">
          <p className="emiCalculatorResultRowText">
            {np ? "ब्याज तिर्न योग्य" : "Interest Payable"}
          </p>
          <p className="emiCalculatorResultRowAns">
            {np ? "रु." : "Rs."}{" "}
            {commaTotalAmountOfInterest ? commaTotalAmountOfInterest : 0}
          </p>
        </div>

        <div className="emiCalculatorResultRowContainer">
          <p className="emiCalculatorResultRowText">
            {np ? "कुल भुक्तानी योग्य रकम" : "Total Amount Payable"}
          </p>
          <p className="emiCalculatorResultRowAns">
            {np ? "रु." : "Rs."} {commatotalAmt ? commatotalAmt : 0}
          </p>
        </div>
      </div>

      <div className="emiCalculatorChartContainer">
        <Doughnut data={chartData} />
      </div>
    </div>
  );
};

export default EMICalculator;
